/**
 * Linked items — the one UI for the unified link graph. Shows what a node is
 * explicitly linked to (chips, × to unlink), a picker to add more, and for
 * nodes with implicit context (a meeting's attendees, their accounts/deals,
 * recent threads) a "Related" strip that can be promoted to an explicit link.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Link2, Plus, X } from "lucide-react";
import { Button, toast } from "@/ui";
import { contacts as contactsApi, linksApi, loopBatch, loopRecords, messages as messagesApi, type LinkNode, type LinkNodeType, type LinkedItem, type LoopRecord } from "../lib/api";
import { useLoopDirectory } from "./loop/useDirectory";
import { useWorkspaceContext } from "@/ui";
import { ContactContextPanel, MailThreadContextPanel } from "../routes/Mailbox";
import EditRecordModal from "./loop/EditRecordModal";
import Typeahead from "./Typeahead";

const KIND_LABEL: Record<LinkNodeType, string> = {
    contact: "Contact", company: "Account", lead: "Deal", event: "Event", meeting: "Meeting",
    record: "Note / task", case: "Case", thread: "Email thread", message: "Email",
};

export function linksQueryKey(type: LinkNodeType, id: string) { return ["links", type, id] as const; }

/**
 * Open a linked thing without leaving the page: contacts and emails go to the
 * workspace context rail (the same panels the mailbox uses), records open
 * their edit modal. Everything else navigates.
 */
function useOpenLinked() {
    const { openContext, closeContext } = useWorkspaceContext();
    const navigate = useNavigate();
    const [editing, setEditing] = useState<LoopRecord | null>(null);
    const open = async (item: LinkedItem) => {
        try {
            if (item.type === "contact") {
                const c = await contactsApi.get(item.id);
                if (!c.email) { navigate(item.href ?? "/contacts"); return; }
                openContext("linked-contact", <ContactContextPanel focus={{ email: c.email, fallbackName: c.name ?? c.email, productKey: c.productKey ?? null }} contact={c} onClose={() => closeContext("linked-contact")} />, "contact details");
                return;
            }
            if (item.type === "message" || item.type === "thread") {
                const m = item.type === "message" ? await messagesApi.get(item.id) : (await messagesApi.list({ threadKey: item.id, limit: "1" })).data[0];
                if (!m) throw new Error("Email not found");
                openContext("linked-mail", <MailThreadContextPanel message={m} onClose={() => closeContext("linked-mail")} />, "email");
                return;
            }
            if (item.type === "record") {
                const r = await loopBatch.getByIds([item.id]);
                if (r.ok && r.records[0]) { setEditing(r.records[0]); return; }
                throw new Error("Record not found");
            }
            if (item.href) navigate(item.href);
        } catch (e) { toast.error(e instanceof Error ? e.message : "Couldn't open"); }
    };
    const modal = editing ? <EditRecordModal record={editing} open onOpenChange={(o) => { if (!o) setEditing(null); }} /> : null;
    return { open, modal };
}

function Chip({ item, onRemove, onAdd, onOpen }: { item: LinkedItem; onRemove?: () => void; onAdd?: () => void; onOpen: (item: LinkedItem) => void }) {
    const body = <>
        <span className="linked-chip-kind">{KIND_LABEL[item.type]}</span>
        <span className="linked-chip-label">{item.label}</span>
        {item.sublabel && <span className="linked-chip-sub">{item.sublabel}</span>}
    </>;
    return (
        <span className={`linked-chip${onAdd ? " linked-chip--related" : ""}`}>
            <button type="button" className="linked-chip-body" onClick={() => onOpen(item)}>{body}</button>
            {onRemove && <button type="button" className="linked-chip-x" title="Unlink" onClick={onRemove}><X size={11} /></button>}
            {onAdd && <button type="button" className="linked-chip-x" title="Link explicitly" onClick={onAdd}><Plus size={11} /></button>}
        </span>
    );
}

export default function LinkedItems({ type, id, compact = false, title = "Linked", variant, exclude }: {
    type: LinkNodeType;
    id: string;
    /** Inline strip (mail message) vs. section with heading (rails). */
    compact?: boolean;
    title?: string;
    /** "rail": full-width rows instead of pills — for narrow side rails. */
    variant?: "rail";
    /** `type:id` keys already visible on this page — not repeated here. */
    exclude?: Set<string>;
}) {
    const queryClient = useQueryClient();
    const { open: openItem, modal } = useOpenLinked();
    const from: LinkNode = { type, id };
    const query = useQuery({ queryKey: linksQueryKey(type, id), queryFn: () => linksApi.for(type, id), staleTime: 30_000 });
    const visible = (i: LinkedItem) => !exclude?.has(i.type + ":" + i.id);
    const items = (query.data?.ok ? query.data.items : []).filter(visible);
    const related = (query.data?.ok ? query.data.related : []).filter(visible);
    const [picking, setPicking] = useState(false);

    const mutate = useMutation({
        mutationFn: ({ add, remove }: { add?: LinkNode[]; remove?: LinkNode[] }) => linksApi.set(from, add, remove),
        onSuccess: (res, vars) => {
            if (res.ok) queryClient.setQueryData(linksQueryKey(type, id), { ...res, related: related.filter((r) => !res.items.some((i) => i.type === r.type && i.id === r.id)) });
            // The other end's list changed too.
            for (const n of [...(vars.add ?? []), ...(vars.remove ?? [])]) queryClient.invalidateQueries({ queryKey: linksQueryKey(n.type, n.id) });
            queryClient.invalidateQueries({ queryKey: ["loop", "today-bundle"] });
        },
    });

    const { directory } = useLoopDirectory();
    const recentQuery = useQuery({ queryKey: ["loop", "recent", "picker"], queryFn: () => loopRecords.recent(undefined, 100), enabled: picking, staleTime: 30_000 });
    const options = useMemo(() => {
        const linked = new Set(items.map((i) => i.type + ":" + i.id));
        const opt = (kind: LinkNodeType, oid: string, label: string) =>
            kind === type && oid === id || linked.has(kind + ":" + oid) ? null : { id: kind + ":" + oid, label, group: KIND_LABEL[kind] };
        return [
            ...directory.contacts.filter((c) => c.id !== "me").map((c) => opt("contact", c.id, c.name)),
            ...directory.targets.map((t) => opt(t.kind as LinkNodeType, t.id, t.name)),
            ...((recentQuery.data?.ok ? recentQuery.data.records : []).map((r) => opt("record", r.id, r.content))),
        ].filter((o): o is { id: string; label: string; group: string } => Boolean(o));
    }, [directory, recentQuery.data, items, type, id]);

    const remove = (i: LinkedItem) => mutate.mutate({ remove: [{ type: i.type, id: i.id }] });
    const add = (n: LinkNode) => { mutate.mutate({ add: [n] }); setPicking(false); };

    const picker = picking
        ? <span className="linked-picker"><Typeahead value="" allowEmpty={false} placeholder="Link to…" options={options}
            onChange={(picked) => { if (!picked) return; const [kind, ...rest] = picked.split(":"); add({ type: kind as LinkNodeType, id: rest.join(":") }); }} /></span>
        : <Button variant="ghost" size="sm" className="linked-add" onClick={() => setPicking(true)} aria-label="Link to…" title="Link to…"><Link2 />{compact ? null : "Link"}</Button>;

    if (compact) {
        return (
            <div className="linked-items linked-items--compact">
                {items.map((i) => <Chip key={i.type + i.id} item={i} onRemove={() => remove(i)} onOpen={openItem} />)}
                {picker}
                {modal}
            </div>
        );
    }
    return (
        <div className={`linked-items${variant === "rail" ? " linked-items--rail" : ""}`}>
            <div className="row" style={{ alignItems: "center", gap: 8 }}>
                <h3 className="loop-eyebrow">{title} · {items.length}</h3>
                {picker}
            </div>
            {items.length > 0 && <div className="linked-chips">{items.map((i) => <Chip key={i.type + i.id} item={i} onRemove={() => remove(i)} onOpen={openItem} />)}</div>}
            {items.length === 0 && !query.isLoading && <p className="muted loop-mp-rail-hint">Nothing linked yet.</p>}
            {related.length > 0 && <>
                <h4 className="linked-related-title">Related</h4>
                <div className="linked-chips">{related.map((i) => <Chip key={i.type + i.id} item={i} onAdd={() => add({ type: i.type, id: i.id })} onOpen={openItem} />)}</div>
            </>}
            {modal}
        </div>
    );
}
